import { Component, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { LoadManagementState } from '../../../modules/load-management/store/load-management.state';
import { VehicleCategory } from '@shared/models/vehicle-category.model';

@Component({
  selector: 'hmt-standard-vehicle-v2',
  standalone: false,
  templateUrl: './standard-vehicle.component.html',
  styleUrls: ['./standard-vehicle.component.scss']
})
export class StandardVehicleComponentV2 {
  form: FormGroup;
  utilization$!: Observable<number>;
  sliderBg$!: Observable<string>;
  standardVehicleCategories$!: Observable<VehicleCategory[]>;
  standardCategories: VehicleCategory[] = [];

  // slider label formatter
  displayPercent(value: number): string {
    return `${value ?? 100}%`;
  }

  // Cost Per KM must be a positive value (empty, 0 and negative values are invalid)
  private static greaterThanZeroValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined || value === '') return { required: true };
    return Number(value) > 0 ? null : { greaterThanZero: true };
  }

  onUtilizationInput(value: number | string): void {
    const num = Number(value);
    const clamped = Math.max(0, Math.min(100, isNaN(num) ? 100 : num));
    this.form.patchValue({ utilization: clamped });
  }

  onQuantityInput(value: number | string): void {
    const num = Number(value);
    const clamped = Math.max(1, isNaN(num) ? 1 : Math.floor(num));
    this.form.patchValue({ quantity: clamped });
  }

  limitDecimalPlaces(event: any, fieldName: string, maxDecimals: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    
    if (!value || value === '') return;
    
    // Check if the value has more than the allowed decimal places
    const parts = value.split('.');
    if (parts.length === 2 && parts[1].length > maxDecimals) {
      // Truncate to max decimal places
      value = parts[0] + '.' + parts[1].substring(0, maxDecimals);
      input.value = value;
      
      // Update form control
      const control = this.form.get(fieldName);
      if (control) {
        control.setValue(parseFloat(value), { emitEvent: false });
      }
    }
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly dialogRef: MatDialogRef<StandardVehicleComponentV2>,
    private readonly store: Store,
    @Inject(MAT_DIALOG_DATA) public data: { label: string; count: number; stateSelector?: any; loadType?: string; defaultUtilization?: number }
  ) {
    const initialUtilization = this.data?.defaultUtilization != null ? Math.max(0, Math.min(100, Number(this.data.defaultUtilization))) : 100;
    // Cost Per KM isn't shown/used for FCL (container) loads, so it stays optional there.
    const costPerKmValidators = this.data?.loadType === 'FCL'
      ? [Validators.min(0)]
      : [StandardVehicleComponentV2.greaterThanZeroValidator];
    this.form = this.formBuilder.group({
      // store the selected category as its name for simple label mapping
      type: [null, [Validators.required]],
      quantity: [1, [Validators.required, Validators.min(1)]],
      costPerKm: [0, costPerKmValidators],
      maxCarryingWeight: [0, [Validators.required, Validators.min(0)]],
      maxCapacity: [0, [Validators.required, Validators.min(0)]],
      utilization: [initialUtilization, [Validators.required, Validators.min(0), Validators.max(100)]]
    });

    const ctrl = this.form.get('utilization');
    this.utilization$ = (ctrl?.valueChanges as Observable<number>).pipe(startWith(ctrl?.value ?? 100));
    this.sliderBg$ = this.utilization$.pipe(
      map((val: number) => {
        const pct = Math.max(0, Math.min(100, Number(val) || 0));
        // Purple to filled percentage, grey for remainder
        return `linear-gradient(90deg, #6F68B9 ${pct}%, rgba(142, 134, 222, 0.2) ${pct}%)`;
      })
    );

    // Use provided state selector or default to LoadManagementState
    const stateSelector = this.data?.stateSelector || LoadManagementState.getStandardVehicleCategories;
    this.standardVehicleCategories$ = this.store.select(stateSelector);

    // cache categories for quick lookup on selection
    this.standardVehicleCategories$.pipe(take(1)).subscribe(cats => {
      this.standardCategories = cats || [];
    });
  }

  increase(): void {
    const current = Number(this.form.value.quantity) || 0;
    this.form.patchValue({ quantity: current + 1 });
  }

  decrease(): void {
    const current = Number(this.form.value.quantity) || 0;
    this.form.patchValue({ quantity: Math.max(1, current - 1) });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.form.valid) {
      const typeLabel = this.form.value.type || 'Vehicle';
      const count = Number(this.form.value.quantity) || 0;
      const costperkm = Number(this.form.value.costPerKm) || 0;
      const maxcarryingweight = Number(this.form.value.maxCarryingWeight) || 0;
      const maxcapacity = Number(this.form.value.maxCapacity) || 0;
      const utilization = Number(this.form.value.utilization) || 100;
      const categoryId = this.standardCategories.find(c => c.categoryName === typeLabel)?.id || null;
      // Return legacy shape expected by caller while dialog UI uses richer form
      this.dialogRef.close({
        label: typeLabel,
        count: count,
        costperkm: costperkm,
        maxcarryingweight: maxcarryingweight,
        maxcapacity: maxcapacity,
        utilization: utilization,
        categoryId: categoryId
      });
    }
  }

  onCategorySelected(categoryName: string): void {
    if (!categoryName) { return; }
    const selected = this.standardCategories.find(c => c.categoryName === categoryName);
    if (!selected) { return; }
    
    // Truncate to 3 decimals when setting from category
    const maxLoadWeight = this.truncateToDecimals(selected.maxLoadWeight ?? 0, 3);
    const maxLoadVolume = this.truncateToDecimals(selected.maxLoadVolume ?? 0, 3);
    
    this.form.patchValue({
      costPerKm: selected.costPerKm ?? 0,
      maxCarryingWeight: maxLoadWeight,
      maxCapacity: maxLoadVolume
      // keep utilization as user-controlled; default remains 0
    });
  }

  private truncateToDecimals(value: number, decimals: number): number {
    const str = value.toString();
    const parts = str.split('.');
    if (parts.length === 1) return value;
    return parseFloat(parts[0] + '.' + parts[1].substring(0, decimals));
  }

  reset(): void {
    this.form.reset({
      type: null,
      quantity: 1,
      costPerKm: 0,
      maxCarryingWeight: 0,
      maxCapacity: 0,
      utilization: 100
    });
  }
}
